import React, { useEffect, useState, useRef, useCallback } from "react";
import { LayoutCommon } from "../../components/layout/Layout";
import { getRecentNews } from "../../data/news";
import { BsArrowRightCircle, BsArrowLeftCircle } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function WhatsHappening() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [displayCount, setDisplayCount] = useState(3);
  const sectionRef = useRef(null); // Ref for the section to animate

  // Fetch news data
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsData = await getRecentNews();
        setNews(newsData);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const sortedNews = news.sort((a, b) => new Date(b.date) - new Date(a.date));
  const totalItems = sortedNews.length;

  // Background colors array using darker config colors for white text readability
  const backgroundColors = ["#0C3F90", "#8E8D8D", "#031026", "#64789C"];

  // Duplicate array to create infinite scrolling effect
  const infiniteNews = [...sortedNews, ...sortedNews];

  // Define handlers with useCallback to prevent infinite re-renders
  const handleNext = useCallback(() => {
    console.log(
      "Next clicked, current index:",
      scrollIndex,
      "total items:",
      totalItems
    );
    setScrollIndex((prevIndex) => (prevIndex + 1) % totalItems);
  }, [scrollIndex, totalItems]);

  const handlePrevious = useCallback(() => {
    console.log(
      "Previous clicked, current index:",
      scrollIndex,
      "total items:",
      totalItems
    );
    setScrollIndex((prevIndex) =>
      prevIndex === 0 ? totalItems - 1 : prevIndex - 1
    );
  }, [scrollIndex, totalItems]);

  // Adjust display count based on window width
  useEffect(() => {
    const updateDisplayCount = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setDisplayCount(1.3);
      } else if (width < 600) {
        setDisplayCount(2);
      } else if (width < 1024) {
        setDisplayCount(2.3);
      } else {
        setDisplayCount(3);
      }
    };

    // Initial call to set display count
    updateDisplayCount();
    // Listen for window resize events
    window.addEventListener("resize", updateDisplayCount);

    return () => window.removeEventListener("resize", updateDisplayCount);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      handleNext();
    }, 3000); // Scroll every 3 seconds
    return () => clearInterval(intervalId);
  }, [handleNext]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString(undefined, options);
  };

  const translateX = `-${scrollIndex * (100 / displayCount)}%`;
  console.log(
    "translateX:",
    translateX,
    "scrollIndex:",
    scrollIndex,
    "displayCount:",
    displayCount
  );

  // GSAP Scroll Animation
  useEffect(() => {
    if (sectionRef.current) {
      gsap.fromTo(
        sectionRef.current.querySelectorAll(".news-item"),
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          stagger: 0.2,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 100%", // Trigger animation when the section is 80% into view
          },
        }
      );
    }
  }, []);
  const handleNavigation = () => {
    window.scrollTo(0, 0); // Scroll to the top
    navigate("/news-and-events"); // Navigate to the News and Events page
  };
  if (loading) {
    return (
      <div>
        <LayoutCommon>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading news...</p>
          </div>
        </LayoutCommon>
      </div>
    );
  }

  return (
    <div className="mb-12 sm:mb-16 md:mb-20 lg:mb-24 xl:mb-32 relative z-10">
      <LayoutCommon>
        <h2 className="leading-relaxed place-self-start text-left text-2xl sm:text-3xl md:text-4xl lg:text-5xl 2xl:text-6xl font-semibold">
          What's Happening
        </h2>
        <p className="mt-2 lg:mt-3">
          Stay informed about our latest news and events as we continue to
          redefine logistics standards and provide innovative solutions.
        </p>
        <div
          className="relative overflow-hidden overflow-x-hidden w-full"
          ref={sectionRef} // Ref for GSAP animation
        >
          {/* Control Buttons - Top Right */}
          <div className="flex justify-end mb-3">
            <div className="flex space-x-3">
              <button
                onClick={handlePrevious}
                className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors duration-300 shadow-sm"
              >
                <BsArrowLeftCircle className="text-gray-600" size={20} />
              </button>
              <button
                onClick={handleNext}
                className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors duration-300 shadow-sm"
              >
                <BsArrowRightCircle className="text-gray-600" size={20} />
              </button>
            </div>
          </div>

          {/* Carousel */}
          <div className="overflow-hidden w-full">
            <div
              className="flex transition-transform duration-500"
              style={{ transform: `translateX(${translateX})` }}
            >
              {infiniteNews.map((newsItem, index) => {
                // Get background color based on index
                const backgroundColor =
                  backgroundColors[index % backgroundColors.length];

                return (
                  <div
                    key={index}
                    className="news-item flex-shrink-0 p-2 mt-2 lg:p-3 box-border"
                    style={{ width: `${100 / displayCount}%` }}
                    onClick={handleNavigation}
                  >
                    <div
                      className="relative h-80 md:h-96 lg:h-[28rem] xl:h-[32rem] rounded-[24px] shadow-lg flex flex-col justify-between p-6 lg:p-8 overflow-hidden group cursor-pointer transition-all duration-500"
                      style={{ backgroundColor }}
                    >
                      {/* Background Image - Hidden by default, shows on hover */}
                      {newsItem.images && newsItem.images[0] && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <img
                            src={newsItem.images[0].img}
                            alt={newsItem.images[0].alt || newsItem.heading}
                            className="w-full h-full object-cover rounded-[24px] transform scale-110 group-hover:scale-100 transition-transform duration-700 ease-out"
                          />
                          {/* Dark overlay for text readability */}
                          <div className="absolute inset-0 bg-black/40 rounded-[24px]"></div>
                        </div>
                      )}
                      {/* Top section with date */}
                      <div className="flex justify-between items-start relative z-10">
                        <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                          {formatDate(newsItem.formattedDate || newsItem.date)}
                        </span>
                        <span className="text-white text-sm font-medium">
                          Read more →
                        </span>
                      </div>

                      {/* Bottom section with content */}
                      <div className="text-white relative z-10">
                        <h3 className="text-xl font-bold mb-3 leading-tight">
                          {newsItem.heading}
                        </h3>
                        <p className="text-sm leading-relaxed opacity-80">
                          {newsItem.description &&
                          newsItem.description.length > 120
                            ? `${newsItem.description.slice(0, 120)}...`
                            : newsItem.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </LayoutCommon>
    </div>
  );
}

export default WhatsHappening;
